import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Key,
  User,
  CheckCircle,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import ProductKey from './ProductKey';
import RegisterAccount from './RegisterAccount';
import { API_BASE } from '../../utils/apiConfig';
import { cacheSettings, readCachedSettings } from '../../../core/contexts/settingsContextUtils';
import { getDefaultSettings } from '../../../core/services/settingsService';
import { QAWAEM_LOGO_FULL } from '../../../products/saudi/config/qawaemAssets';
import { SAUDI_BRAND } from '../../../products/saudi/config/saudiBrandConfig';
import '../../../products/saudi/layout/saudi-qawaem.css';

/** Same favicon as `<link rel="icon" />` in `public/index.html`. */
const QAWAEM_FAVICON_SRC = encodeURI(
  `${process.env.PUBLIC_URL ?? ''}/qawaem/favicon.png`
);

async function tryCreateAdminOnApi({ username, email, password }) {
  if (!API_BASE) return false;
  try {
    const response = await fetch(`${API_BASE}/settings/admin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, email, password }),
    });
    if (!response.ok) {
      console.warn('Setup: backend admin create returned', response.status);
      return false;
    }
    const result = await response.json();
    console.log('✅ Admin account saved to backend:', result);
    return true;
  } catch (error) {
    console.warn('Setup: backend unavailable, using offline admin storage:', error?.message || error);
    return false;
  }
}

function persistAdminSetupLocally(setupData) {
  const adminEmail = setupData.adminEmail?.trim() || '';
  const companyPhone = setupData.companyPhone?.trim() || '';
  const entityName = setupData.companyEntityName?.trim() || '';

  const adminUser = {
    username: setupData.adminUsername,
    email: adminEmail,
    phone: companyPhone,
    password: setupData.adminPassword,
    role: 'admin',
    createdAt: new Date().toISOString(),
    isActive: true,
  };

  localStorage.setItem('qawaem_admin_user', JSON.stringify(adminUser));

  if (adminEmail && /\S+@\S+\.\S+/.test(adminEmail)) {
    localStorage.setItem('qawaem_admin_email_hint', adminEmail);
  }

  localStorage.setItem(
    'setup_data',
    JSON.stringify({
      company: {
        name: entityName,
        phone: companyPhone,
        email: adminEmail,
      },
    })
  );

  const current = readCachedSettings() || getDefaultSettings();
  cacheSettings({
    ...current,
    hasAdminUser: true,
    adminUser: {
      ...(current.adminUser || {}),
      username: adminUser.username,
      email: adminEmail,
      phone: companyPhone,
      password: setupData.adminPassword,
      role: 'admin',
    },
    company: {
      ...(current.company || {}),
      name: entityName || current.company?.name || '',
      phone: companyPhone || current.company?.phone || '',
      email: adminEmail || current.company?.email || '',
    },
  });

  return adminUser;
}

const SetupWizard = ({ variant = 'qawaem' }) => {
  const { t } = useTranslation();
  const isQawaem = variant === 'qawaem';
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [licenseValidated, setLicenseValidated] = useState(false); // 🛡️ SÉCURITÉ: Tracker la validation de licence

  // 🔄 VÉRIFIER LICENCE ET COMPTE ADMIN AU DÉMARRAGE
  useEffect(() => {
    const checkExistingSetup = async () => {
      try {
        // 1. Vérifier la licence
        const licenseData = localStorage.getItem('qawaem_license');
        let hasValidLicense = false;
        
        if (licenseData) {
          const license = JSON.parse(licenseData);
          if (license.status === 'active') {
            hasValidLicense = true;
            setLicenseValidated(true);
          }
        }
        
        if (hasValidLicense) {
          // 2. Vérifier si un compte admin existe déjà
          const adminUser = localStorage.getItem('qawaem_admin_user');
          let hasAdminAccount = false;
          
          // Vérifier dans localStorage d'abord
          if (adminUser) {
            try {
              const admin = JSON.parse(adminUser);
              if (admin.username && admin.username.trim() !== '' && admin.password && admin.password.trim() !== '') {
                hasAdminAccount = true;
              }
            } catch (error) {
              console.error('Error parsing admin user from localStorage:', error);
            }
          }
          
          // Vérifier aussi via settingsService si nécessaire
          if (!hasAdminAccount) {
            try {
              const { default: settingsService } = await import('../../../core/services/settingsService');
              const settings = await settingsService.get();
              if (settings?.adminUser?.username && settings.adminUser.username.trim() !== '' && 
                  settings?.adminUser?.password && settings.adminUser.password.trim() !== '') {
                hasAdminAccount = true;
              }
            } catch (e) {
              console.warn('Unable to verify settings:', e);
            }
          }
          
          if (hasAdminAccount) {
            console.log('🔑 License and admin account found - redirecting to login page');
            // Marquer le setup comme terminé et rediriger vers login
            localStorage.setItem('qawaem_setup_completed', 'true');
            navigate('/login', { replace: true });
          } else {
            console.log('🔑 License found but no admin account - creating account');
            setCurrentStep(2); // aller à création compte
          }
        }
      } catch (error) {
        console.error('Setup verification error:', error);
      }
    };
    
    checkExistingSetup();
  }, []);

  const [setupData, setSetupData] = useState({
    user: {
      username: '',
      fullName: '',
      email: '',
      password: '',
      confirmPassword: ''
    }
  });

  // Helper function to update nested data - 🔧 MEMOIZED to prevent re-renders
  const updateData = useCallback((path, value) => {
    // Safety check: ensure path is a string
    if (typeof path !== 'string') {
      console.error('updateData: path must be a string, received:', typeof path, path);
      return;
    }
    
    setSetupData(prev => {
      // Handle flat paths (no dots) or nested paths (with dots)
      if (!path.includes('.')) {
        // Flat path - update directly at root level
        return { ...prev, [path]: value };
      }
      
      // Nested path - split and navigate
      const keys = path.split('.');
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  }, []);

  // 🔄 Callback pour la validation de licence - 🔧 MEMOIZED to prevent re-renders
  const onLicenseValidated = useCallback(async () => {
    console.log('🎉 License validated - checking admin account...');
    setLicenseValidated(true);
    
    // Vérifier si un compte admin existe déjà
    const adminUser = localStorage.getItem('qawaem_admin_user');
    let hasAdminAccount = false;
    
    // Vérifier dans localStorage d'abord
    if (adminUser) {
      try {
        const admin = JSON.parse(adminUser);
        if (admin.username && admin.username.trim() !== '' && admin.password && admin.password.trim() !== '') {
          hasAdminAccount = true;
        }
      } catch (error) {
        console.error('Error parsing admin user during license validation:', error);
      }
    }
    
    // Vérifier aussi via settingsService si nécessaire
    if (!hasAdminAccount) {
      try {
        const { default: settingsService } = await import('../../../core/services/settingsService');
        const settings = await settingsService.get();
        if (settings?.adminUser?.username && settings.adminUser.username.trim() !== '' && 
            settings?.adminUser?.password && settings.adminUser.password.trim() !== '') {
          hasAdminAccount = true;
        }
      } catch (e) {
        console.warn('Unable to verify settings after license validation:', e);
      }
    }
    
    if (hasAdminAccount) {
      console.log('🚀 Admin account found - redirecting to login page');
      // Marquer le setup comme terminé et rediriger vers login
      localStorage.setItem('qawaem_setup_completed', 'true');
      navigate('/login', { replace: true });
    } else {
      console.log('🚀 No admin account - redirecting to account creation');
      setCurrentStep(2); // aller à création compte
    }
  }, []);

  // 🔧 PLUS DE WRAPPER FUNCTIONS - évite les re-renders inutiles

  const steps = [
    {
      id: 'welcome',
      title: t('setup.step_welcome'),
      icon: Sparkles,
      component: 'WelcomeStep'
    },
    {
      id: 'license',
      title: t('setup.step_license'),
      icon: Key,
      component: 'ProductKey'
    },
    {
      id: 'profile',
      title: t('setup.step_profile'),
      icon: User,
      component: 'RegisterAccount'
    }
  ];

  const nextStep = async () => {
    // If on the last step, complete the setup
    if (currentStep === steps.length - 1) {
      try {
        console.log('📝 Completing setup - saving admin account...');

        persistAdminSetupLocally(setupData);

        await tryCreateAdminOnApi({
          username: setupData.adminUsername,
          email: setupData.adminEmail?.trim() || '',
          password: setupData.adminPassword,
        });

        try {
          const settingsService = (await import('../../../core/services/settingsService')).default;
          const current = await settingsService.get();
          const entityName = setupData.companyEntityName?.trim() || '';
          const companyPhone = setupData.companyPhone?.trim() || '';
          const adminEmail = setupData.adminEmail?.trim() || '';

          await settingsService.update({
            ...current,
            hasAdminUser: true,
            adminUser: {
              ...(current.adminUser || {}),
              username: setupData.adminUsername,
              email: adminEmail,
              phone: companyPhone,
              password: setupData.adminPassword,
            },
            company: {
              ...(current.company || {}),
              name: entityName || current.company?.name || '',
              phone: companyPhone || current.company?.phone || '',
              email: adminEmail || current.company?.email || '',
            },
          });
        } catch (e) {
          console.warn('Setup: settings API sync skipped (offline mode):', e?.message || e);
        }

        try {
          const licensingService = (await import('../../../core/services/licensingService')).default;
          const syncResult = await licensingService.syncClientProfileToConsole({
            email: setupData.adminEmail?.trim() || '',
            phone: setupData.companyPhone?.trim() || '',
            clientName: setupData.companyEntityName?.trim() || '',
          });
          if (syncResult.success) {
            console.log('✅ Client profile synced to Console');
          } else {
            console.warn('Console client sync skipped or failed:', syncResult.reason || syncResult.data);
          }
        } catch (syncErr) {
          console.warn('Console client sync error:', syncErr?.message || syncErr);
        }

        localStorage.setItem('qawaem_setup_completed', 'true');
        console.log('🔑 Setup completed - redirecting to login');
        navigate('/login', { replace: true });
      } catch (error) {
        console.error('❌ Error completing setup:', error);
        window.alert(t('setup.setup_complete_error'));
      }
      return;
    }
    
    // Otherwise, go to next step
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    // 🛡️ SÉCURITÉ: Empêcher le retour en arrière après validation de licence
    if (currentStep === 2 && licenseValidated) {
      console.warn('🚫 Back navigation blocked: License already validated and consumed');
      return;
    }

    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (stepIndex) => {
    // 🛡️ SÉCURITÉ: Empêcher la navigation vers des étapes non autorisées
    
    // 🚫 Si licence validée, empêcher retour aux étapes précédentes
    if (licenseValidated && stepIndex < 2) {
      console.warn('🚫 Navigation blocked: License already validated and consumed');
      return;
    }

    if (stepIndex === 0) {
      // Seulement si licence non validée
      if (!licenseValidated) {
        setCurrentStep(stepIndex);
      }
    } else if (stepIndex === 1) {
      // Seulement si licence non validée
      if (!licenseValidated && currentStep >= 1) {
        setCurrentStep(stepIndex);
      }
    } else if (stepIndex === 2) {
      // ⚠️ ÉTAPE COMPTE: Seulement accessible si la licence est validée
      if (licenseValidated) {
        setCurrentStep(stepIndex);
      } else {
        console.warn('🚫 Access denied: License not validated');
        return;
      }
    }
  };

  // 🔧 RENDER COMPONENT BASED ON CURRENT STEP - évite les wrappers
  const renderCurrentComponent = () => {
    const currentStepData = steps[currentStep];
    if (!currentStepData) return null;

    switch (currentStepData.component) {
      case 'WelcomeStep':
        return (
          <WelcomeStep 
            nextStep={nextStep}
            prevStep={prevStep}
            currentStep={currentStep}
            totalSteps={steps.length}
            variant={variant}
          />
        );
      case 'ProductKey':
        return (
          <ProductKey 
            onLicenseValidated={onLicenseValidated}
            nextStep={nextStep}
            prevStep={prevStep}
            currentStep={currentStep}
            totalSteps={steps.length}
            variant={variant}
          />
        );
      case 'RegisterAccount':
        return (
          <RegisterAccount 
            data={setupData}
            updateData={updateData}
            nextStep={nextStep}
            prevStep={prevStep}
            currentStep={currentStep}
            totalSteps={steps.length}
            variant={variant}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={isQawaem
      ? 'qawaem-setup-shell qawaem-preauth flex min-h-[100dvh] w-full flex-col items-stretch overflow-x-hidden px-0 pt-2 pb-0 sm:px-1 sm:pb-4 sm:pt-3 md:px-2 md:py-6 lg:px-3 lg:py-8'
      : 'qawaem-preauth-route flex min-h-[100dvh] w-full flex-col items-stretch overflow-x-hidden bg-gradient-to-br from-slate-900 via-blue-750 to-slate-900 px-0 pt-2 pb-0 sm:px-1 sm:pb-4 sm:pt-3 md:px-2 md:py-6 lg:px-3 lg:py-8'}>
      <div className="mx-auto flex w-full min-w-0 max-w-[100vw] flex-1 min-h-0 flex-col gap-2 px-0 sm:gap-4 md:gap-5">
        <div className="flex w-full shrink-0 select-none justify-center gap-2 px-3 overflow-x-auto pb-1 sm:gap-4 sm:px-4 sm:pb-2">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            const lineDone = index < currentStep;

            if (isQawaem) {
              return (
                <div key={step.id} className="flex shrink-0 items-center gap-3">
                  <motion.button
                    type="button"
                    title={step.title}
                    onClick={() => goToStep(index)}
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.96 }}
                    className={[
                      'qawaem-setup-step',
                      isCompleted && 'is-done',
                      isActive && !isCompleted && 'is-active',
                      !isActive && !isCompleted && 'is-idle',
                    ].filter(Boolean).join(' ')}
                  >
                    {isCompleted ? (
                      <CheckCircle className="h-5 w-5" aria-hidden />
                    ) : (
                      <Icon className="h-5 w-5" aria-hidden />
                    )}
                  </motion.button>
                  {index < steps.length - 1 && (
                    <div
                      role="presentation"
                      className={[
                        'hidden h-[3px] min-w-[1.75rem] max-w-[3.5rem] flex-1 rounded-full sm:inline-block lg:min-w-[3rem]',
                        lineDone ? 'qawaem-setup-line is-done' : 'qawaem-setup-line is-idle',
                      ].join(' ')}
                    />
                  )}
                </div>
              );
            }

            return (
              <div key={step.id} className="flex shrink-0 items-center gap-3">
                <motion.button
                  type="button"
                  title={step.title}
                  onClick={() => goToStep(index)}
                  whileHover={{ scale: 1.06 }}
                  whileTap={{ scale: 0.96 }}
                  className={[
                    'flex h-11 w-11 items-center justify-center rounded-xl border-2 transition-colors',
                    isCompleted && 'border-emerald-400/70 bg-emerald-500/20 text-emerald-200 shadow-lg shadow-emerald-500/10',
                    isActive &&
                      !isCompleted &&
                      'border-blue-400 bg-blue-500/20 text-blue-50 shadow-lg shadow-blue-500/20 ring-2 ring-blue-400/40 ring-offset-2 ring-offset-slate-950',
                    !isActive &&
                      !isCompleted &&
                      'border-white/15 bg-white/5 text-white/35 hover:border-white/25 hover:text-white/60'
                  ].filter(Boolean).join(' ')}
                >
                  {isCompleted ? (
                    <CheckCircle className="h-5 w-5" aria-hidden />
                  ) : (
                    <Icon className="h-5 w-5" aria-hidden />
                  )}
                </motion.button>
                {index < steps.length - 1 && (
                  <div
                    role="presentation"
                    className={[
                      'hidden h-[3px] min-w-[1.75rem] max-w-[3.5rem] flex-1 rounded-full sm:inline-block lg:min-w-[3rem]',
                      lineDone ? 'bg-emerald-400/50' : 'bg-white/10'
                    ].join(' ')}
                  />
                )}
              </div>
            );
          })}
        </div>

        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="flex min-h-0 w-full flex-1 flex-col"
        >
          {renderCurrentComponent()}
        </motion.div>
      </div>
    </div>
  );
};

/** Étape bienvenue — cartes glass Qawaem ou Qawaem sombre selon variant. */
const WelcomeStep = ({ nextStep, variant = 'qawaem' }) => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`setup.welcomeStep.${key}`, opts);
  const isQawaem = variant === 'qawaem';
  /** `vw` dans les tailles de texte ne suit pas le zoom navigateur ; ici tout repose sur rem/em pour garder cartes + typo proportionnels au zoom. */
  const features = isQawaem
    ? [
        {
          Icon: Key,
          title: 'نظام التراخيص',
          desc: 'تفعيل آمن وإدارة ترخيص المؤسسة.',
          gradient: 'from-emerald-500 to-emerald-600',
        },
        {
          Icon: User,
          title: 'حسابات المستخدمين',
          desc: 'مسؤول النظام وصلاحيات واضحة.',
          gradient: 'from-amber-500 to-amber-600',
        },
        {
          Icon: Sparkles,
          title: 'واجهة عربية حديثة',
          desc: 'تجربة موحّدة مع وحدات الأعمال السعودية.',
          gradient: 'from-green-600 to-green-700',
        },
      ]
    : [
    {
      Icon: Key,
      title: p('feature_licensing_title'),
      desc: p('feature_licensing_desc'),
      gradient: 'from-blue-500 to-blue-600',
    },
    {
      Icon: User,
      title: p('feature_users_title'),
      desc: p('feature_users_desc'),
      gradient: 'from-emerald-500 to-emerald-600',
    },
    {
      Icon: Sparkles,
      title: p('feature_ui_title'),
      desc: p('feature_ui_desc'),
      gradient: 'from-amber-500 to-amber-600',
    },
  ];

  if (isQawaem) {
    return (
      <div className="glass-modal relative flex h-full min-h-0 w-full min-w-0 max-w-none flex-1 flex-col overflow-hidden p-6 sm:p-8 md:p-10">
        <div className="relative flex min-h-0 flex-1 flex-col items-center text-center">
          <motion.img
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 220, damping: 18 }}
            src={QAWAEM_LOGO_FULL}
            alt={SAUDI_BRAND.appName}
            className="mb-4 h-14 w-auto object-contain sm:mb-5 sm:h-16"
            draggable={false}
          />

          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl font-bold text-console sm:text-4xl"
          >
            {SAUDI_BRAND.appName}
          </motion.h1>
          <p className="mt-1 text-sm font-medium text-console-muted sm:mt-2 sm:text-base">
            {SAUDI_BRAND.appSubtitle}
          </p>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mt-4 max-w-2xl text-sm leading-relaxed text-console-muted sm:text-base"
          >
            نظام تخطيط موارد المؤسسة للسوق السعودي — مخزون، مبيعات، محاسبة، موارد بشرية والمزيد.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 }}
            className="mt-8 grid w-full min-w-0 grid-cols-1 gap-4 md:grid-cols-3"
          >
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.08 }}
                className="glass-panel p-4 text-right text-sm"
              >
                <div className={`mb-3 inline-flex rounded-xl bg-gradient-to-r p-2 ${f.gradient}`}>
                  <f.Icon className="h-5 w-5 text-white" aria-hidden />
                </div>
                <h3 className="text-base font-semibold text-console">{f.title}</h3>
                <p className="mt-1 text-console-muted text-sm">{f.desc}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.button
            type="button"
            onClick={nextStep}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="mt-8 inline-flex items-center justify-center gap-2 rounded-xl px-8 py-3 text-base font-semibold btn-primary-forest"
          >
            <Sparkles className="h-5 w-5 shrink-0" aria-hidden />
            بدء التثبيت
            <ArrowRight className="h-5 w-5 shrink-0" aria-hidden />
          </motion.button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-full min-h-0 w-full min-w-0 max-w-none flex-1 flex-col overflow-hidden rounded-none border-x-0 border-y border-white/15 bg-white/10 shadow-xl shadow-black/20 backdrop-blur-lg sm:rounded-3xl sm:border sm:border-white/20 sm:shadow-black/25">
      <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-emerald-500/15 blur-3xl" />

      <div className="relative flex min-h-0 flex-1 flex-col px-3 py-3 sm:px-4 sm:py-4 md:px-5 md:py-5 lg:px-6 xl:px-8 2xl:px-10">
        <div className="flex flex-col items-center text-center">
          <motion.img
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 220, damping: 18 }}
            src={QAWAEM_FAVICON_SRC}
            alt="Qawaem"
            className="mb-4 h-14 w-14 shrink-0 object-contain drop-shadow-[0_8px_24px_rgb(15_23_42/0.55)] sm:mb-5 sm:h-16 sm:w-16 md:h-[72px] md:w-[72px] lg:h-20 lg:w-20"
            draggable={false}
          />

          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl font-bold text-white sm:text-4xl md:text-5xl"
          >
            Qawaem
          </motion.h1>
          <p className="mt-1 text-sm font-medium text-white/65 sm:mt-2 sm:text-base md:text-lg">
            {p('platform_subtitle')}
          </p>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mt-3 w-full max-w-none px-1 text-xs leading-relaxed text-white/55 sm:mt-4 sm:text-sm md:text-base"
          >
            {p('platform_desc')}&nbsp;
            <span className="text-white/70">
              {p('platform_modules')}
            </span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 }}
            className="mt-6 grid w-full min-w-0 grid-cols-1 gap-[0.75rem] sm:mt-8 sm:gap-4 md:mt-10 md:grid-cols-3 md:gap-x-6 lg:gap-x-10 xl:gap-x-14 2xl:gap-x-16"
          >
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.08 }}
                whileHover={{ y: -4 }}
                className="min-w-0 rounded-[0.875rem] border border-white/20 bg-white/5 p-[1em] text-left text-[0.8125rem] shadow-lg shadow-black/10 backdrop-blur-md sm:rounded-[1rem] sm:p-[1.125em] sm:text-[0.875rem] md:text-[0.9375rem]"
              >
                <div
                  className={`mb-[0.65em] inline-flex rounded-[0.65em] bg-gradient-to-r p-[0.45em] shadow-md sm:mb-[0.75em] sm:rounded-[0.7em] sm:p-[0.5em] ${f.gradient}`}
                >
                  <f.Icon className="h-[1.45em] w-[1.45em] shrink-0 text-white" aria-hidden />
                </div>
                <h3 className="text-[1.08em] font-semibold leading-snug text-white">{f.title}</h3>
                <p className="mt-[0.4em] text-[0.88em] leading-snug text-white/55">{f.desc}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.button
            type="button"
            onClick={nextStep}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="mt-6 inline-flex w-full max-w-md items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 sm:mt-8 sm:max-w-none sm:gap-3 sm:px-8 sm:py-3.5 sm:text-base md:mt-10 md:inline-flex md:w-auto"
          >
            <Sparkles className="h-5 w-5 shrink-0" aria-hidden />
            {p('start_install')}
            <ArrowRight className="h-5 w-5 shrink-0" aria-hidden />
          </motion.button>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.55 }}
            className="mt-6 flex w-full max-w-none flex-wrap items-center justify-center gap-6 border-t border-white/10 pt-6 text-[11px] text-white/45 sm:mt-8 sm:justify-between sm:gap-4 sm:px-2 sm:pt-8 sm:text-xs md:text-sm lg:gap-8 xl:gap-12"
          >
            <div className="min-w-[5rem] flex-1 text-center sm:flex-none">
              <div className="text-base font-bold text-white sm:text-lg">500+</div>
              <div>{p('stat_companies')}</div>
            </div>
            <div className="hidden h-8 w-px shrink-0 bg-white/10 sm:block" />
            <div className="min-w-[5rem] flex-1 text-center sm:flex-none">
              <div className="text-sm font-bold text-white sm:text-base md:text-lg">99.9%</div>
              <div>{p('stat_availability')}</div>
            </div>
            <div className="hidden h-8 w-px shrink-0 bg-white/10 sm:block" />
            <div className="min-w-[5rem] flex-1 text-center sm:flex-none">
              <div className="text-sm font-bold text-white sm:text-base md:text-lg">24/7</div>
              <div>{p('stat_support')}</div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SetupWizard;
